/**
 * Links para compartir una búsqueda, un resort o una semana de Weeks.
 *
 * El link nunca lleva el token: la URL del portal trae `?token=` con la sesión del
 * socio, así que se arma desde los datos de la búsqueda y no desde `location.href`.
 * Quien lo abre sin sesión pasa por el login de su club con `?weeksPath=` y, al
 * entrar, regresa a la misma ruta.
 */

// Campos de `searchWeek` que viajan tal cual en la URL
const CAMPOS_BUSQUEDA = ["country", "state", "city", "checkIn", "checkOut"];
const CAMPOS_FILTRO = [
  "type",
  "capacity",
  "occupancy",
  "kitchen",
  "resort",
  "cityFilter",
  "priceMin",
  "priceMax",
  "rating",
];
const PARAMS_COMPARTIBLES = [...CAMPOS_BUSQUEDA, ...CAMPOS_FILTRO, "holiday", "sort"];

// Rutas a las que el login puede regresar después de iniciar sesión
const RUTAS_PERMITIDAS = ["/resortlist", "/resort", "/holidaylist"];

const vacio = (v) =>
  v === null || v === undefined || v === "" || (Array.isArray(v) && !v.length);

// Los filtros de la barra lateral son arreglos (tipo, recámaras, cocina…): viajan como JSON
const valorParam = (v) => (Array.isArray(v) ? JSON.stringify(v) : String(v));

const linkBusqueda = (busqueda = {}, { sortId = null, resort = null } = {}) => {
  const params = new URLSearchParams();
  for (const campo of [...CAMPOS_BUSQUEDA, ...CAMPOS_FILTRO, "holiday"]) {
    if (!vacio(busqueda[campo])) params.set(campo, valorParam(busqueda[campo]));
  }
  if (sortId) params.set("sort", sortId);
  if (resort) params.set("resort", resort);
  return `${window.location.origin}/resortlist?${params.toString()}`;
};

// Por el id de inventario de RCI y no por el _id: el _id cambia en cada descarga de RCI
const linkSemana = (week = {}, busqueda = {}) => {
  const params = new URLSearchParams();
  params.set("weekInventoryId", week.InventoryID);
  const checkIn = busqueda.checkIn || week.CheckInDate;
  const checkOut = busqueda.checkOut || week.CheckOutDate;
  if (checkIn) params.set("checkIn", checkIn);
  if (checkOut) params.set("checkOut", checkOut);
  for (const campo of ["country", "state", "city"]) {
    if (!vacio(busqueda[campo])) params.set(campo, busqueda[campo]);
  }
  if (week.ResortName) params.set("resort", week.ResortName);
  return `${window.location.origin}/resort?${params.toString()}`;
};

/**
 * La ruta que llega al login en `weeksPath`. Solo rutas propias de Weeks: el host lo
 * pone el código, nunca el parámetro, para que nadie arme un link que mande el token
 * del socio a otro sitio.
 * @returns {string|null}
 */
const weeksPathValido = (valor) => {
  if (!valor || typeof valor !== "string") return null;
  if (!valor.startsWith("/") || valor.startsWith("//") || valor.includes("\\")) return null;
  if (/^\/*[a-z][a-z0-9+.-]*:/i.test(valor)) return null;
  const ruta = valor.split("?")[0];
  return RUTAS_PERMITIDAS.includes(ruta) ? valor : null;
};

/**
 * La ruta actual sin token ni idioma, solo si es algo compartido (una búsqueda o una
 * semana). En cualquier otra ruta regresa null y el flujo de sesión no cambia.
 */
const rutaCompartida = () => {
  const { pathname, search } = window.location;
  if (!RUTAS_PERMITIDAS.includes(pathname)) return null;
  const params = new URLSearchParams(search);
  params.delete("token");
  params.delete("lang");
  const tieneBusqueda = PARAMS_COMPARTIBLES.some((p) => params.has(p)) || params.has("weekInventoryId");
  if (!tieneBusqueda) return null;
  return `${pathname}?${params.toString()}`;
};

// `https://{host}{ruta}` con el token agregado a la query de la ruta
const urlConToken = (host, ruta, token, extra = {}) => {
  const [path, query] = ruta.split("?");
  const params = new URLSearchParams(query || "");
  params.set("token", token);
  for (const [k, v] of Object.entries(extra)) if (!vacio(v)) params.set(k, v);
  return `https://${host}${path}?${params.toString()}`;
};

export {
  CAMPOS_FILTRO,
  PARAMS_COMPARTIBLES,
  linkBusqueda,
  linkSemana,
  weeksPathValido,
  rutaCompartida,
  urlConToken,
};
